import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Yakureki, YakurekiDetail } from '@/shared/types/yakurekiCore'
import { Soaps } from '@/shared/types/yakurekiSoap'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { usePatientListQuery } from '@/features/patients/hooks/queries/usePatientListQuery'
import { usePatientDetailQuery } from '@/features/patients/hooks/queries/usePatientDetailQuery'
import { useScreenMode, ScreenMode } from '@/shared/hooks/useScreenMode'
import { useMyPharmacyQuery } from '@/features/yakureki/hooks/queries/useMyPharmacyQuery'
import { getGuidanceSupportData } from '@/apps/legacy/features/guidance/apis/guidance'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'

// UUIDフォーマットの検証
const isValidRequestId = (id: string): boolean => {
  if (id === 'new') return true
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(id)
}

export interface PatientPageState {
  // 状態（統合された状態）
  status:
    | 'initial-loading' // 初回ロード中
    | 'loading-detail' // 詳細データロード中
    | 'switching-patient' // 患者切り替え中
    | 'ready' // 表示準備完了
    | 'error' // エラー状態

  // データ
  patientList: Yakureki[]
  selectedPatient?: Yakureki
  patientDetail?: YakurekiDetail
  soapList?: Omit<Soaps, 'id'>

  // エラー情報（統合されたエラー）
  error?: string

  // 画面モード
  screenMode: ScreenMode

  // アクション
  selectPatient: (patientId: string) => void
  reloadPatientList: () => Promise<void>
  reloadPatientDetail: () => Promise<void>
}

/**
 * 統合的な患者ページ状態管理フック（統合レイヤー）
 * - 新しい専用フックを組み合わせて統一されたインターフェースを提供
 * - 既存のコンポーネントとの互換性を維持
 */
export const usePatientPageState = (requestId?: string): PatientPageState => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // 専用フックの使用
  const patientListHook = usePatientListQuery()
  const patientList = patientListHook.data ?? []
  const patientDetailHook = usePatientDetailQuery(requestId, patientList)
  const { transcriptionMode } = useMyPharmacyQuery()
  const screenModeHook = useScreenMode({
    patientDetail: patientDetailHook.patientDetail,
    selectedPatient: patientDetailHook.selectedPatient,
    patientListLoading: patientListHook.isLoading,
    patientDetailLoading: patientDetailHook.isLoading,
    transcriptionMode,
  })

  // 患者選択アクション.
  // 楽観 cache + fire-and-forget GET で指導支援の生成 trigger を先行させ、 遷移先で
  // 即「生成中」 skeleton 表示 → polling に乗せて race condition を回避する.
  const selectPatient = useCallback(
    (patientId: string) => {
      if (!isValidRequestId(patientId)) {
        console.warn(`Invalid request ID format: ${patientId}`)
        return
      }

      // 指導支援データの生成を先行 trigger (fire-and-forget).
      getGuidanceSupportData(patientId).catch(() => {})

      // 楽観的にキャッシュセット → 遷移先で即 polling 開始.
      queryClient.setQueryData(queryKeys.guidance.detail(patientId), {
        data: null,
        isGenerating: true,
      })

      localStorageWrapper.setItem('lastAccessedRequestId', patientId)
      navigate(`/soap/${patientId}`)
    },
    [navigate, queryClient]
  )

  // 統合された状態の計算
  const status: PatientPageState['status'] = (() => {
    if (patientListHook.isLoading && patientList.length === 0) {
      return 'initial-loading'
    }
    if (patientDetailHook.isLoading) {
      return requestId === patientDetailHook.selectedPatient?.id
        ? 'loading-detail'
        : 'switching-patient'
    }
    if (patientListHook.error?.message || patientDetailHook.error) {
      return 'error'
    }
    return 'ready'
  })()

  // 統合されたエラー（TanStack Query の Error を文字列に変換）
  const patientListError = patientListHook.error?.message
  const patientDetailError = patientDetailHook.error?.message
  const error = patientListError || patientDetailError

  return {
    status,
    patientList,
    selectedPatient: patientDetailHook.selectedPatient,
    patientDetail: patientDetailHook.patientDetail,
    soapList: patientDetailHook.soapList,
    error,
    screenMode: screenModeHook.screenMode,
    selectPatient,
    reloadPatientList: patientListHook.reloadPatientList,
    reloadPatientDetail: patientDetailHook.reloadPatientDetail,
  }
}
